import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Mock unfurl service for the LinkCard concept.
 *
 * A link preview is a LIFECYCLE, not two static layouts. The real service is
 * server-side (SSRF-guarded, oEmbed → OpenGraph → Twitter Cards → <title>,
 * cached ~24h with a stored `fetchedAt` and a manual re-unfurl affordance — see
 * note 52104 §B3). This is a promise-based fake with a configurable delay and a
 * failure toggle so the story can drive the whole lifecycle without a network.
 *
 * The data arrives on a PROGRESSIVE LADDER of three tiers, each carrying more
 * than the last:
 *   url    → just the raw URL (nothing fetched yet)
 *   basic  → favicon + title + domain
 *   full   → + description, hero image, media kind / duration
 * The card reserves the next tier's space at every tier, so climbing the ladder
 * never reflows the card.
 */

export type UnfurlTier = "url" | "basic" | "full";

export type UnfurlStatus = "resolving" | "resolved" | "stale" | "error";

/** The cumulative data shape. Fields fill in as the ladder climbs. */
export interface UnfurlData {
  /** Always present — the raw pasted URL. */
  url: string;
  /** Tier `basic` and up: a stand-in favicon colour. */
  faviconColor?: string;
  /** Tier `basic` and up: page title. */
  title?: string;
  /** Tier `basic` and up: the display domain. */
  domain?: string;
  /** Tier `full`: short description / summary. */
  description?: string;
  /** Tier `full`: hero image src (a real <img> in production). */
  heroSrc?: string;
  /** Tier `full`: hero fill colour, used when there is no hero image. */
  heroColor?: string;
  /** Tier `full`: media kind chip (Video, Article, …). */
  kind?: string;
  /** Tier `full`: duration chip for playable media. */
  duration?: string;
  /** Tier `full`: whether the media is playable (shows the cover play badge). */
  playable?: boolean;
}

/** A seed link — what the fake "server" would resolve the URL to at tier full. */
export interface MockLink {
  url: string;
  faviconColor: string;
  title: string;
  domain: string;
  description: string;
  heroColor: string;
  heroSrc?: string;
  kind: string;
  duration?: string;
  playable?: boolean;
}

export const MOCK_LINKS: Record<string, MockLink> = {
  vimeo: {
    url: "https://vimeo.com/876631121",
    faviconColor: "#0F6E56",
    title: "Brand refresh — motion explorations for the spring launch",
    domain: "vimeo.com",
    description:
      "A reel of motion studies exploring the refreshed brand system: type in motion, the new mark, and transitions for the marketing site.",
    heroColor: "#1D9E75",
    kind: "Video",
    duration: "4:32",
    playable: true,
  },
  history: {
    url: "https://ww2history.org/timeline",
    faviconColor: "#8C5A2B",
    title:
      "The Second World War • Timeline, key battles, and firsthand accounts",
    domain: "ww2history.org",
    description:
      "An illustrated timeline of the Second World War with primary sources, maps, and firsthand accounts from each theatre of the war.",
    heroColor: "#B5793B",
    kind: "Article",
    playable: false,
  },
};

/** Progressive tier data derived from a seed link. */
const tierData = (link: MockLink, tier: UnfurlTier): UnfurlData => {
  if (tier === "url") return { url: link.url };
  if (tier === "basic") {
    return {
      url: link.url,
      faviconColor: link.faviconColor,
      title: link.title,
      domain: link.domain,
    };
  }
  return { ...link };
};

export interface UnfurlConfig {
  /** Which seed link to resolve. */
  link: MockLink;
  /** Per-tier delay in ms (url→basic and basic→full each wait this long). */
  delayMs?: number;
  /** When true, the unfurl fails partway and lands in the error state. */
  shouldFail?: boolean;
}

export interface UnfurlController {
  status: UnfurlStatus;
  tier: UnfurlTier;
  data: UnfurlData;
  /** Epoch ms of the last successful resolve (drives the stale affordance). */
  fetchedAt: number | null;
  /** Start (or restart) the unfurl ladder from scratch. */
  start: () => void;
  /** Retry after an error — re-runs the simulation. */
  retry: () => void;
  /** Re-unfurl a resolved/stale card — re-runs the simulation. */
  reunfurl: () => void;
  /** Force the resolved card into the stale state (demo affordance). */
  markStale: () => void;
}

/**
 * Drives one link through the unfurl lifecycle. Climbs url → basic → full on a
 * timer, or lands in `error` if `shouldFail`. `retry` / `reunfurl` re-run it;
 * `markStale` moves a resolved card to the stale state so the re-unfurl
 * affordance can be shown.
 */
export const useUnfurl = ({
  link,
  delayMs = 900,
  shouldFail = false,
}: UnfurlConfig): UnfurlController => {
  const [status, setStatus] = useState<UnfurlStatus>("resolving");
  const [tier, setTier] = useState<UnfurlTier>("url");
  const [data, setData] = useState<UnfurlData>(() => tierData(link, "url"));
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);

  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  const start = useCallback(() => {
    clearTimers();
    setStatus("resolving");
    setTier("url");
    setData(tierData(link, "url"));

    if (shouldFail) {
      // Fail partway: the favicon + title never arrive; we keep the raw URL.
      timers.current.push(setTimeout(() => setStatus("error"), delayMs));
      return;
    }

    timers.current.push(
      setTimeout(() => {
        setTier("basic");
        setData(tierData(link, "basic"));
      }, delayMs)
    );
    timers.current.push(
      setTimeout(() => {
        setTier("full");
        setData(tierData(link, "full"));
        setStatus("resolved");
        setFetchedAt(Date.now());
      }, delayMs * 2)
    );
  }, [clearTimers, link, delayMs, shouldFail]);

  const markStale = useCallback(() => {
    setStatus((prev) => (prev === "resolved" ? "stale" : prev));
  }, []);

  // Kick off on mount and whenever the config that defines the run changes.
  useEffect(() => {
    start();
    return clearTimers;
  }, [start, clearTimers]);

  return {
    status,
    tier,
    data,
    fetchedAt,
    start,
    retry: start,
    reunfurl: start,
    markStale,
  };
};

/** Human-readable "fetched N ago" for the stale affordance. */
export const formatFetchedAt = (fetchedAt: number | null): string => {
  if (!fetchedAt) return "";
  const seconds = Math.max(1, Math.round((Date.now() - fetchedAt) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};
